import type { ReactNode } from "react";

import { PlatformIcon } from "@cntrl/ui";
import styles from "./DspMiniCard.module.scss";

/**
 * One non-Spotify DSP card (Apple Music / Amazon Music) — ONE implementation, rendered by the dashboard's
 * Audience rail and by the Streaming page's DSP row.
 *
 * **Why it is shared.** Both read the same `DspCard` DTO off the same `BuildDspCard` parse, and both had
 * hand-rolled a card for it. The dashboard's was a proper platform card — white paper, the platform's colour
 * down the left edge, the brand mark beside the name. The Streaming page's was a bare bordered box with no
 * background at all, so it sat directly on the page canvas (`--ac-color-bg`, a cool light grey) and read as
 * an unfinished well rather than a card. This is the dashboard's treatment, adopted for both.
 *
 * **The brand mark leads the name, in the card's leading corner** — the dashboard's `PlatformHead` lockup,
 * adopted rather than re-invented. The socials 2x2 sits directly below these cards in the same rail and uses
 * that lockup; putting the mark anywhere else leaves two card families in one section out of step. (Built
 * top-RIGHT first, on the reasoning that an absolute mark lets every name start at the same text edge — which
 * is true, and still the wrong trade against matching the cards immediately beneath it.)
 *
 * **A knowingly deliberate divergence from the legacy app**, which grouped these under an "Other DSPs"
 * heading with a prose sub-line about the data ceiling. That heading is gone: two cards that state their own
 * platform do not need a label telling you they are platforms.
 *
 * **A cell with no value is DROPPED, and a card with no cells does not render** — the B-3 rule. These sources
 * answer different things (Amazon has followers, Apple does not), so a fixed pair painted `Current —` on an
 * Apple doc that carried only an editorial count. The legacy worker pushed only non-zero cells for the same
 * reason. A platform doc existing is not itself a number worth a tile.
 *
 * **It is also the dashboard's socials 2x2** (2026-09-01). Those four tiles had a hand-rolled copy of this
 * card's chrome that had drifted in every inset it owned — half the block padding, a third of the gap, and a
 * `min-block-size` standing in for the height the padding no longer gave it — so the rail read as two card
 * families rather than one. They are this component now, at `as="li"` inside the grid; the ONLY thing the
 * grid changes is the width. The two behavioural differences the socials tiles need are expressed at the
 * CALL SITE rather than as modes here: a tile keeps its `—` (it passes the dash as the cell's value, so the
 * drop-empty rule above never fires) and it carries a footnote (it passes `foot`, which costs no height).
 */
export function DspMiniCard({
  platform,
  name,
  cells,
  foot,
  as: Tag = "div",
}: {
  /** Platform key — drives the brand mark and the edge colour (`amazon`, `apple`, …). */
  platform: string;
  name: string;
  /**
   * Pre-FORMATTED values, not raw numbers: these cards print exact separated counts (`11,944`) and ratio
   * labels (`9 / 12`), neither of which a numeric formatter can produce. A KPI a manager reads out on a call
   * has to be the real number, so the caller formats and this renders. `null` drops the cell.
   */
  cells: ReadonlyArray<{ label: string; value: string | null }>;
  /**
   * Optional footnote under the figures — the socials tile's "lifetime views" / "last post" line, rendered
   * through `DspMiniFoot`.
   *
   * **It costs the card NO height**: the band is positioned inside the card's own bottom padding, so a card
   * that carries one is exactly as tall as a card that does not, and four tiles are level whether or not a
   * given platform has a footnote. Nothing here needs to be passed to hold space.
   */
  foot?: ReactNode;
  /** `li` when the card IS the grid item — the socials 2x2, whose `<ul>` owns the grid. */
  as?: "div" | "li";
}) {
  const shown = cells.filter((c) => c.value != null);
  if (shown.length === 0) return null;

  return (
    <Tag className={styles.card} data-platform={platform}>
      {/* The brand mark leads the name in a header row — the dashboard's own `PlatformHead` lockup. The
          socials 2x2 sits directly under these cards in the same rail and uses that lockup, so a mark in any
          other corner would put two card families in the same section out of step with each other.
          `aria-hidden` because the name beside it already says which platform this is. */}
      <header className={styles.head}>
        <span className={styles.icon} aria-hidden="true">
          <PlatformIcon platform={platform} size={24} />
        </span>
        <h4 className={styles.name}>{name}</h4>
      </header>
      <div className={styles.stats}>
        {shown.map((c) => (
          <div key={c.label} className={styles.stat}>
            <span className={styles.statLabel}>{c.label}</span>
            <span className={styles.statValue}>{c.value}</span>
          </div>
        ))}
      </div>
      {foot !== undefined && <div className={styles.feet}>{foot}</div>}
    </Tag>
  );
}

/**
 * One line in a card's footnote band. ONE class for every footnote a card can carry — the socials tiles'
 * two (lifetime views, and Instagram's last-post stamp) used to be separate rules that had drifted apart in
 * COLOUR, so the same slot read differently depending on which platform you looked at.
 */
export function DspMiniFoot({ children }: { children: ReactNode }) {
  return <span className={styles.foot}>{children}</span>;
}
